import type { LocalPrinterMapping } from "@/utils/hardwareBridge"
import { getLocalPrintCapabilities } from "@/api/business/printing"
import { hardwareBridge } from "@/utils/hardwareBridge"

const RESERVED_LEGACY_TYPES = new Set(["RECEIPT", "INVOICE"])
const CAPABILITIES_CACHE_MS = 60_000
let cached: { scope: string, expiresAt: number, value: Awaited<ReturnType<typeof getLocalPrintCapabilities>> } | undefined
let pending: { scope: string, promise: ReturnType<typeof getLocalPrintCapabilities> } | undefined
let generation = 0

export function invalidateLocalPrinterCapabilities() {
  generation++
  cached = undefined
  pending = undefined
}

function fetchCapabilities(scope?: string): ReturnType<typeof getLocalPrintCapabilities> {
  if (!scope) return getLocalPrintCapabilities()
  if (cached?.scope === scope && Date.now() < cached.expiresAt) return Promise.resolve(cached.value)
  if (pending?.scope === scope) return pending.promise
  if ((cached && cached.scope !== scope) || (pending && pending.scope !== scope)) invalidateLocalPrinterCapabilities()
  const version = generation
  const promise = getLocalPrintCapabilities().then((value) => {
    if (version === generation && value.code === 0) cached = { scope, value, expiresAt: Date.now() + CAPABILITIES_CACHE_MS }
    return value
  }).finally(() => {
    if (pending?.promise === promise) pending = undefined
  })
  pending = { scope, promise }
  return promise
}

export interface MultiLocalPrinterCapabilities {
  enabled: boolean
  deviceDeliveryEnabled: boolean
  helperSupported: boolean
  mappings: LocalPrinterMapping[]
  localPrinterKeys: string[]
}

export async function loadMultiLocalPrinterCapabilities(scope?: string): Promise<MultiLocalPrinterCapabilities> {
  const [backendResult, helperResult] = await Promise.allSettled([
    fetchCapabilities(scope),
    hardwareBridge.getPrinterMappings(),
  ])
  const backendSupported = backendResult.status === "fulfilled"
    && backendResult.value.code === 0
    && backendResult.value.data?.claimVersion === 2
    && backendResult.value.data?.multiLocalPrinterEnabled === true
  const helperSupported = helperResult.status === "fulfilled"
  const deviceDeliveryEnabled = backendResult.status === "fulfilled"
    && backendResult.value.code === 0
    && backendResult.value.data?.deviceDeliveryVersion === 1

  if (!backendSupported || !helperSupported) {
    return {
      enabled: false,
      deviceDeliveryEnabled,
      helperSupported,
      mappings: [],
      localPrinterKeys: [],
    }
  }

  const mappings = helperResult.value
    .filter(mapping => !RESERVED_LEGACY_TYPES.has(mapping.type.toUpperCase()))
  return {
    enabled: true,
    deviceDeliveryEnabled,
    helperSupported: true,
    mappings,
    localPrinterKeys: mappings
      .filter(mapping => mapping.available)
      .map(mapping => mapping.type),
  }
}
